import * as React from 'react';

import { Dispatch } from 'redux';
import { useLocation } from 'react-router';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import styled from 'styled-components';

import User from '@common/types/user/user';
import { UPDATE_USER } from '@webapp/store/user/actions';
import { CHANGE_LOGIN } from '@webapp/store/auth/actions';

import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import UserProfileEditorCtrl from '@webapp/controllers/user-profile/user-editor/index';
import { Translate } from 'react-localize-redux';
import { ADD_PAYMENT_METHOD_ERROR } from '@webapp/store/payment/actions';
import SimpleArtist from '@common/types/artist/simple-artist';
import { TracPage } from '@services/tracpage/types/trac-page';
import { UpdateUser } from '@webapp/store/user/action-creators';
import { CloseMenu } from '@webapp/store/ui/action-creators';

interface ProfileGeneralPageProps {
  className?: string;
  isLoading: boolean;
  isMobile?: boolean;
  artist: SimpleArtist;
  saveUser: (userData: Partial<User>, redirectedFrom?: string) => UpdateUser;
  artistTracPage: TracPage;
  closeAllMenus: () => CloseMenu;
}

const mapStateToProps = (state: AppState) => ({
  artist: state.artist.active,
  errorMsg: state.ui.errors[ADD_PAYMENT_METHOD_ERROR] || '',
  isMobile: state.ui.mobile,
  isLoading: [UPDATE_USER, CHANGE_LOGIN].some(
    (event) => state.ui.loading[event]
  ),
  artistTracPage: state.tracpage.artistPage,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  saveUser: (userData: Partial<User>, redirectedFrom?: string) =>
    dispatch(new UpdateUser(userData, false, redirectedFrom)),
  closeAllMenus: () => dispatch(new CloseMenu()),
});

export const ProfileGeneralPage: React.FC<ProfileGeneralPageProps> = (
  props
) => {
  const location = useLocation();
  let pageSource =
    new URLSearchParams(location.search).get('pageSource') || undefined;

  const [userData, setUserData] = React.useState<Partial<User>>();
  const { saveUser, closeAllMenus, isLoading } = props;
  const [isSaving, setIsSaving] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);

  React.useEffect(() => {
    if (isLoading) {
      setIsSaving(false);
      setDisabled(false);
    }
  }, [isSaving, disabled, isLoading]);

  const saveChanges = () => {
    if (!userData) {
      return;
    }
    saveUser(userData, pageSource);
    closeAllMenus();
  };

  const handleChanges = (data: Partial<User>, isNameUpdate: boolean) => {
    if (isNameUpdate) {
      setUserData({
        ...userData,
        fullName: data?.fullName || '',
        avatar: data?.avatar || '',
      });
      return;
    }
    setUserData(data);
  };

  return (
    <MotionOpacity>
      <div className={props.className}>
        {!props.isMobile && (
          <Typography
            className="mb-20"
            type="subheader"
            weight="400"
            customFont="semi-bold"
            id="general"
          >
            <Translate id="USER.SETTINGS.GENERAL.TITLE" />
          </Typography>
        )}

        <UserProfileEditorCtrl
          className="mb-20"
          showWalletAddressBtn={!!pageSource}
          onChange={handleChanges}
          onSaveChanges={() => saveChanges()}
        />
      </div>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ProfileGeneralPage)`
  margin-bottom: 100px;
  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    margin-bottom: 0;
  }
`);
